import * as React from 'react';
import { type StyleProp, StyleSheet, Text, View, type ViewStyle } from 'react-native';

import { type IconDefinition, renderIcon, Tooltip } from '@gitbook/spine-react';

import { Interactive } from '../Interactive';
import { useCurrentTheme } from '../theme';
import {
    BADGE_HEIGHT,
    BADGE_ICON_SIZE,
    BADGE_ICON_SPACING,
    BADGE_KIND_STYLES,
    BADGE_LABEL_H_PADDING,
    BADGE_TEXT_SIZE,
    type BadgeKind,
    type BadgeSize,
} from './common';

export interface BadgeLabelProps {
    kind?: BadgeKind;
    size?: BadgeSize;
    icon?: IconDefinition;
    children: React.ReactNode;
    style?: StyleProp<ViewStyle>;
    tooltip?: React.ReactNode;
}

/**
 * BadgeLabel adds metadata or indicates the status of items and navigational elements.
 * They shouldn't have any behaviour expectations.
 */
export const BadgeLabel = React.forwardRef(function BadgeLabelWithRef(
    props: BadgeLabelProps,
    ref: React.Ref<View>
): React.ReactElement {
    const { icon, kind = 'secondary', size = 'large', children, style, tooltip, ...rest } = props;
    const [, styles] = useCurrentTheme(BADGE_KIND_STYLES[kind]);

    return (
        <Tooltip tooltip={tooltip}>
            <Interactive.View
                ref={ref}
                style={[
                    staticStyles.badge,
                    styles.wrapper,
                    {
                        minWidth: BADGE_HEIGHT[size],
                        height: BADGE_HEIGHT[size],
                        paddingHorizontal: BADGE_LABEL_H_PADDING[size],
                    },
                    style,
                ]}
                {...rest}
            >
                {icon ? renderIcon(icon, { size: BADGE_ICON_SIZE[size] }) : null}
                {children ? (
                    <View>
                        <Text
                            style={[
                                BADGE_TEXT_SIZE[size],
                                styles.text,
                                icon ? BADGE_ICON_SPACING[size] : undefined,
                            ]}
                        >
                            {children}
                        </Text>
                    </View>
                ) : null}
            </Interactive.View>
        </Tooltip>
    );
});

const staticStyles = StyleSheet.create({
    badge: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 100,
        borderWidth: 1,
        width: 'fit-content',
        textAlignVertical: 'center',
        textAlign: 'center',
    },
});
